import {bg, da, et, fi, hr, hu, lt, lv, nl, pt, ro, sl, sv} from 'date-fns/locale';
import {DayOfWeek, type LocaleConfig} from 'platform/locale';

// Dutch follows nl-BE (Belgian market), the same locale as the digital certificate translations.
const defaultLocaleConfigNl: LocaleConfig = {
  language: 'nl',
  dateFnsLocale: nl,
  timezone: 'Europe/Brussels',
  dateTimeFormat: {
    timeShort: 'HH:mm',
    timeMedium: 'HH:mm:ss',
    timeLong: 'HH:mm:ss z',
    timeFull: 'HH:mm:ss zzzz',
    dateShort: 'd/MM/yyyy',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'd/MM/yyyy HH:mm',
    dateTimeMedium: 'd. MMMM yyyy HH:mm',
    dateTimeLong: 'd. MMMM yyyy HH:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy HH:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '.',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '.',
    decimalSeparator: ',',
    position: 'before',
  },
};

const defaultLocaleConfigSv: LocaleConfig = {
  language: 'sv',
  dateFnsLocale: sv,
  timezone: 'Europe/Stockholm',
  dateTimeFormat: {
    timeShort: 'HH:mm',
    timeMedium: 'HH:mm:ss',
    timeLong: 'HH:mm:ss z',
    timeFull: 'HH:mm:ss zzzz',
    dateShort: 'yyyy-MM-dd',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'yyyy-MM-dd HH:mm',
    dateTimeMedium: 'd. MMMM yyyy HH:mm',
    dateTimeLong: 'd. MMMM yyyy HH:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy HH:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigRo: LocaleConfig = {
  language: 'ro',
  dateFnsLocale: ro,
  timezone: 'Europe/Bucharest',
  dateTimeFormat: {
    timeShort: 'HH:mm',
    timeMedium: 'HH:mm:ss',
    timeLong: 'HH:mm:ss z',
    timeFull: 'HH:mm:ss zzzz',
    dateShort: 'dd.MM.yyyy',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'dd.MM.yyyy HH:mm',
    dateTimeMedium: 'd. MMMM yyyy HH:mm',
    dateTimeLong: 'd. MMMM yyyy HH:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy HH:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '.',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '.',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigHu: LocaleConfig = {
  language: 'hu',
  dateFnsLocale: hu,
  timezone: 'Europe/Budapest',
  dateTimeFormat: {
    timeShort: 'H:mm',
    timeMedium: 'H:mm:ss',
    timeLong: 'H:mm:ss z',
    timeFull: 'H:mm:ss zzzz',
    dateShort: 'yyyy. MM. dd.',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'yyyy. MM. dd. H:mm',
    dateTimeMedium: 'd. MMMM yyyy H:mm',
    dateTimeLong: 'd. MMMM yyyy H:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy H:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigSl: LocaleConfig = {
  language: 'sl',
  dateFnsLocale: sl,
  timezone: 'Europe/Ljubljana',
  dateTimeFormat: {
    timeShort: 'HH:mm',
    timeMedium: 'HH:mm:ss',
    timeLong: 'HH:mm:ss z',
    timeFull: 'HH:mm:ss zzzz',
    dateShort: 'd. M. yyyy',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'd. M. yyyy HH:mm',
    dateTimeMedium: 'd. MMMM yyyy HH:mm',
    dateTimeLong: 'd. MMMM yyyy HH:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy HH:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '.',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '.',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigHr: LocaleConfig = {
  language: 'hr',
  dateFnsLocale: hr,
  timezone: 'Europe/Zagreb',
  dateTimeFormat: {
    timeShort: 'HH:mm',
    timeMedium: 'HH:mm:ss',
    timeLong: 'HH:mm:ss z',
    timeFull: 'HH:mm:ss zzzz',
    dateShort: 'dd. MM. yyyy.',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'dd. MM. yyyy. HH:mm',
    dateTimeMedium: 'd. MMMM yyyy HH:mm',
    dateTimeLong: 'd. MMMM yyyy HH:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy HH:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '.',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '.',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigBg: LocaleConfig = {
  language: 'bg',
  dateFnsLocale: bg,
  timezone: 'Europe/Sofia',
  dateTimeFormat: {
    timeShort: 'H:mm',
    timeMedium: 'H:mm:ss',
    timeLong: 'H:mm:ss z',
    timeFull: 'H:mm:ss zzzz',
    dateShort: "d.MM.yyyy 'г.'",
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: "d.MM.yyyy 'г.' H:mm",
    dateTimeMedium: 'd. MMMM yyyy H:mm',
    dateTimeLong: 'd. MMMM yyyy H:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy H:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigLt: LocaleConfig = {
  language: 'lt',
  dateFnsLocale: lt,
  timezone: 'Europe/Vilnius',
  dateTimeFormat: {
    timeShort: 'HH:mm',
    timeMedium: 'HH:mm:ss',
    timeLong: 'HH:mm:ss z',
    timeFull: 'HH:mm:ss zzzz',
    dateShort: 'yyyy-MM-dd',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'yyyy-MM-dd HH:mm',
    dateTimeMedium: 'd. MMMM yyyy HH:mm',
    dateTimeLong: 'd. MMMM yyyy HH:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy HH:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigLv: LocaleConfig = {
  language: 'lv',
  dateFnsLocale: lv,
  timezone: 'Europe/Riga',
  dateTimeFormat: {
    timeShort: 'HH:mm',
    timeMedium: 'HH:mm:ss',
    timeLong: 'HH:mm:ss z',
    timeFull: 'HH:mm:ss zzzz',
    dateShort: 'dd.MM.yyyy',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'dd.MM.yyyy HH:mm',
    dateTimeMedium: 'd. MMMM yyyy HH:mm',
    dateTimeLong: 'd. MMMM yyyy HH:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy HH:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigEt: LocaleConfig = {
  language: 'et',
  dateFnsLocale: et,
  timezone: 'Europe/Tallinn',
  dateTimeFormat: {
    timeShort: 'HH:mm',
    timeMedium: 'HH:mm:ss',
    timeLong: 'HH:mm:ss z',
    timeFull: 'HH:mm:ss zzzz',
    dateShort: 'dd.MM.yyyy',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'dd.MM.yyyy HH:mm',
    dateTimeMedium: 'd. MMMM yyyy HH:mm',
    dateTimeLong: 'd. MMMM yyyy HH:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy HH:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigDa: LocaleConfig = {
  language: 'da',
  dateFnsLocale: da,
  timezone: 'Europe/Copenhagen',
  dateTimeFormat: {
    timeShort: 'HH.mm',
    timeMedium: 'HH.mm.ss',
    timeLong: 'HH.mm.ss z',
    timeFull: 'HH.mm.ss zzzz',
    dateShort: 'dd.MM.yyyy',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'dd.MM.yyyy HH.mm',
    dateTimeMedium: 'd. MMMM yyyy HH.mm',
    dateTimeLong: 'd. MMMM yyyy HH.mm.ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy HH.mm.ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '.',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '.',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigPt: LocaleConfig = {
  language: 'pt',
  dateFnsLocale: pt,
  timezone: 'Europe/Lisbon',
  dateTimeFormat: {
    timeShort: 'HH:mm',
    timeMedium: 'HH:mm:ss',
    timeLong: 'HH:mm:ss z',
    timeFull: 'HH:mm:ss zzzz',
    dateShort: 'dd/MM/yyyy',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'dd/MM/yyyy HH:mm',
    dateTimeMedium: 'd. MMMM yyyy HH:mm',
    dateTimeLong: 'd. MMMM yyyy HH:mm:ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy HH:mm:ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.SUNDAY,
  },
  number: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
    position: 'after',
  },
};

const defaultLocaleConfigFi: LocaleConfig = {
  language: 'fi',
  dateFnsLocale: fi,
  timezone: 'Europe/Helsinki',
  dateTimeFormat: {
    timeShort: 'H.mm',
    timeMedium: 'H.mm.ss',
    timeLong: 'H.mm.ss z',
    timeFull: 'H.mm.ss zzzz',
    dateShort: 'd.M.yyyy',
    dateMedium: 'd. MMMM yyyy',
    dateLong: 'EE, d. MMMM yyyy',
    dateFull: 'EEEE, d. MMMM yyyy',
    dateTimeShort: 'd.M.yyyy H.mm',
    dateTimeMedium: 'd. MMMM yyyy H.mm',
    dateTimeLong: 'd. MMMM yyyy H.mm.ss',
    dateTimeFull: 'EEEE, d. MMMM yyyy H.mm.ss',
    dateRelative: 'relative',
  },
  calendar: {
    firstDayOfTheWeek: DayOfWeek.MONDAY,
  },
  number: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
  },
  currency: {
    thousandsSeparator: '\xa0',
    decimalSeparator: ',',
    position: 'after',
  },
};

/**
 * Digital certificate locale configs for languages outside Omnetic UI's `AppLanguage`.
 *
 * Values follow Unicode CLDR: number and currency separators, currency position, short 24h time,
 * short date with a 4-digit year and first day of the week. `_formatNumber` always groups
 * thousands, so 4-digit numbers are grouped even where CLDR uses minimum grouping digits 2
 * (hu, sl, bg, lv, et, pt).
 */
export const additionalLocaleConfigs = {
  nl: defaultLocaleConfigNl,
  sv: defaultLocaleConfigSv,
  ro: defaultLocaleConfigRo,
  hu: defaultLocaleConfigHu,
  sl: defaultLocaleConfigSl,
  hr: defaultLocaleConfigHr,
  bg: defaultLocaleConfigBg,
  lt: defaultLocaleConfigLt,
  lv: defaultLocaleConfigLv,
  et: defaultLocaleConfigEt,
  da: defaultLocaleConfigDa,
  pt: defaultLocaleConfigPt,
  fi: defaultLocaleConfigFi,
} as const;
